/**
 * One rule for a well-formed MCP pair in an agent document (the Claude Managed Agents
 * agent YAML): every `mcp_servers` entry is `{type: url, name, url}` and is referenced by
 * exactly one `mcp_toolset`, and every toolset names a declared server. CMA rejects an
 * agent with an unreferenced server, so "off" keeps both entries and sets the toolset's
 * `default_config.enabled: false` (`mcpToolsetExposesTools`). The Builder, draft save,
 * publish and the engines read this module instead of each keeping a copy. The rule ids
 * are V2 Studio's AS-MCP-030..034 (agent-studio validation/engine.ts), plus 035 for the
 * document's shape.
 *
 * There is no library allowlist (owner decision 2026-10-12): any URL that passes
 * `mcpServerUrlIssue` may run on every engine. A key may ride in the URL's query — the
 * keyed URL is a sanctioned credential path — but userinfo (`user:pass@`) never may.
 *
 * Dependency-free plain TypeScript on purpose (attachment-manifest.ts rule): both the API
 * and the web app import it, and neither should inherit a zod version through it.
 */
/** The most MCP servers one agent may declare. */
export const MCP_SERVERS_MAX = 20;
/** A server name: what `mcp_servers[].name` holds and `mcp_toolset.mcp_server_name` references. */
export const MCP_SERVER_NAME = /^[A-Za-z0-9_-]{1,64}$/;
/** The longest MCP server URL. */
export const MCP_SERVER_URL_MAX = 2048;
/** The policy of a toolset that names none — CMA's documented default, on every engine. */
export const MCP_TOOLSET_DEFAULT_POLICY = "always_ask";
const MCP_RULES = {
    no_toolset: "AS-MCP-030",
    unknown_server: "AS-MCP-031",
    duplicate: "AS-MCP-032",
    url: "AS-MCP-033",
    credentials: "AS-MCP-034",
    shape: "AS-MCP-035",
};
const record = (value) => typeof value === "object" && value !== null && !Array.isArray(value)
    ? value
    : null;
// The WHATWG parser rewrites every IPv4 spelling (0x7f.1, 2130706433) to dotted decimal,
// and an IPv6 host keeps its brackets, so these two shapes are every IP literal.
const IPV4_HOST = /^\d{1,3}(\.\d{1,3}){3}$/;
/**
 * Why `url` cannot be an MCP server URL, or null when it can: https on port 443 (or none)
 * to a named host — no localhost, no IP literal — at most 2048 characters, and no
 * userinfo. A query is fine, credential-like or not: a key may ride there.
 */
export function mcpServerUrlIssue(url) {
    if (url.length > MCP_SERVER_URL_MAX)
        return { problem: "url", message: `the URL is longer than ${MCP_SERVER_URL_MAX} characters` };
    let parsed;
    try {
        parsed = new URL(url);
    }
    catch {
        return { problem: "url", message: "the URL is not a valid URL" };
    }
    if (parsed.username !== "" || parsed.password !== "")
        return {
            problem: "credentials",
            message: "the URL must not embed credentials (user:password@); a key may ride in the query",
        };
    if (parsed.protocol !== "https:")
        return { problem: "url", message: "the URL must use https" };
    if (parsed.port !== "")
        return { problem: "url", message: "the URL must use port 443" };
    const host = parsed.hostname.replace(/\.$/, "");
    if (host === "localhost" || host.endsWith(".localhost"))
        return { problem: "url", message: "the URL must not point at localhost" };
    if (host.startsWith("[") || IPV4_HOST.test(host))
        return { problem: "url", message: "the URL must name a host, not an IP address" };
    return null;
}
/**
 * Whether a toolset hands the agent any tool: on unless `default_config.enabled` is false,
 * and an allowlist (`enabled: false` plus per-tool `enabled: true`) still exposes the tools
 * it lists. A toolset that exposes nothing is "off": no engine contacts its server.
 */
export function mcpToolsetExposesTools(raw) {
    const toolset = record(raw);
    if (!toolset)
        return false;
    if (record(toolset.default_config)?.enabled !== false)
        return true;
    const configs = Array.isArray(toolset.configs) ? toolset.configs : [];
    return configs.some((entry) => record(entry)?.enabled === true);
}
/**
 * Every way the MCP region of an agent document (the parsed `agent.yaml`) is malformed, in
 * document order: the servers (shape, URL, credentials, duplicate names), then the toolsets
 * (unknown or duplicate targets), then the servers no toolset references. Empty when the
 * region is well formed, and for a document with no MCP at all.
 */
export function mcpDocumentIssues(agent) {
    const document = record(agent);
    if (!document)
        return [];
    const issues = [];
    const add = (problem, server, path, message) => {
        issues.push({ rule: MCP_RULES[problem], problem, server, path, message });
    };
    let servers = [];
    if (document.mcp_servers !== undefined) {
        if (Array.isArray(document.mcp_servers))
            servers = document.mcp_servers;
        else
            add("shape", null, "mcp_servers", "mcp_servers must be a list");
    }
    if (servers.length > MCP_SERVERS_MAX)
        add("shape", null, "mcp_servers", `an agent declares at most ${MCP_SERVERS_MAX} MCP servers, not ${servers.length}`);
    // name → index of its first declaration
    const declared = new Map();
    servers.forEach((entry, index) => {
        const at = `mcp_servers[${index}]`;
        const server = record(entry);
        if (!server) {
            add("shape", null, at, "an MCP server is {type: url, name, url}");
            return;
        }
        const name = typeof server.name === "string" ? server.name : null;
        const label = name === null ? "an MCP server" : `MCP server "${name}"`;
        if (name === null)
            add("shape", null, `${at}.name`, "an MCP server needs a name");
        else if (!MCP_SERVER_NAME.test(name))
            add("shape", name, `${at}.name`, `${label}: a name is 1 to 64 of A-Z a-z 0-9 _ -`);
        if (server.type !== "url")
            add("shape", name, `${at}.type`, `${label} needs type: url`);
        if (typeof server.url !== "string")
            add("shape", name, `${at}.url`, `${label} needs a url`);
        else {
            const urlIssue = mcpServerUrlIssue(server.url);
            if (urlIssue)
                add(urlIssue.problem, name, `${at}.url`, `${label}: ${urlIssue.message}`);
        }
        if (name === null)
            return;
        if (declared.has(name))
            add("duplicate", name, `${at}.name`, `MCP server "${name}" is declared more than once`);
        else
            declared.set(name, index);
    });
    const referenced = new Set();
    const tools = Array.isArray(document.tools) ? document.tools : [];
    tools.forEach((entry, index) => {
        const tool = record(entry);
        if (tool?.type !== "mcp_toolset")
            return;
        const at = `tools[${index}].mcp_server_name`;
        const name = typeof tool.mcp_server_name === "string" && tool.mcp_server_name.length > 0
            ? tool.mcp_server_name
            : null;
        if (name === null)
            add("unknown_server", null, at, "an mcp_toolset needs mcp_server_name");
        else if (!declared.has(name))
            add("unknown_server", name, at, `mcp_toolset targets "${name}", which is not in mcp_servers`);
        else if (referenced.has(name))
            add("duplicate", name, at, `more than one mcp_toolset targets MCP server "${name}"`);
        else
            referenced.add(name);
    });
    for (const [name, index] of declared) {
        if (referenced.has(name))
            continue;
        add("no_toolset", name, `mcp_servers[${index}].name`, `MCP server "${name}" has no mcp_toolset (to switch it off, keep one with default_config.enabled: false)`);
    }
    return issues;
}
//# sourceMappingURL=agent-mcp.js.map